import { useState } from 'react';
import { useTasks } from '@/api/use-tasks';
import { readRememberedTeam, rememberTeam } from '@/tasks/remembered-team';
import type { Task, TeamId } from '@/tasks/task';
import { isMenuAvailable, Sidebar, type AutomationId, type MenuId } from '@/components/Sidebar';
import { AutomationRun, AUTOMATIONS } from '@/pages/AutomationRun';
import { KindsSettings } from '@/pages/KindsSettings';
import { MembersSettings } from '@/pages/MembersSettings';
import { CLOSING_FMT } from '@/fmt/fmt-variant';
import { MonthlyAccounting } from '@/pages/MonthlyAccounting';
import { MonthlyProgress } from '@/pages/MonthlyProgress';
import { RelatedLinks } from '@/pages/RelatedLinks';
import { TaskAnalysis } from '@/pages/TaskAnalysis';
import { TaskManagement } from '@/pages/TaskManagement';
import { TeamSelect } from '@/pages/TeamSelect';

/**
 * App の props。
 */
export interface AppProps {
  /** 初期表示のメニュー */
  initialMenu?: MenuId;
  /**
   * 初期表示のチーム。
   *
   * 指定しないとチーム選択の画面から始まる（どちらのチームを見ているかは画面全体の
   * 前提なので、中に入る前に決めてもらう）。
   */
  initialTeam?: TeamId;
}

/**
 * 各画面へ渡すもの。
 *
 * タスクの一覧はアプリで 1 つだけ持つ。月次経理から起こしたタスクがタスク管理に
 * すぐ出るようにするため、状態はここに置いて下へ渡す。
 */
interface PageContext {
  /** 選択中のチーム */
  team: TeamId;
  /** タスクの一覧 */
  tasks: readonly Task[];
  /** タスクを追加するときに呼ぶ。保存が終わるまで待てるよう Promise を返す */
  onCreateTasks: (created: readonly Omit<Task, 'id'>[]) => Promise<boolean>;
  /** タスクを直したときに呼ぶ。書けたかどうかを返す（失敗の理由は上の帯に出る） */
  onUpdateTask: (task: Task) => Promise<boolean>;
  /** タスクを読み直す。カレンダー取り込みのように、画面の外で増えたときに呼ぶ */
  onReloadTasks: () => void;
  /** タスクを消すときに呼ぶ。消せたかどうかを返す（失敗の理由は上の帯に出る） */
  onDeleteTask: (id: string) => Promise<boolean>;
}

/**
 * メニューと画面の対応。項目を足すときはここへ 1 行加える。
 * @graph-connects none
 */
const PAGES: Partial<Record<MenuId, (context: PageContext) => React.ReactElement>> = {
  tasks: ({ team, tasks, onUpdateTask, onCreateTasks, onReloadTasks, onDeleteTask }) => (
    <TaskManagement
      team={team}
      tasks={tasks}
      onUpdateTask={onUpdateTask}
      onCreateTasks={onCreateTasks}
      onReloadTasks={onReloadTasks}
      onDeleteTask={onDeleteTask}
    />
  ),
  analysis: ({ team, tasks }) => <TaskAnalysis team={team} tasks={tasks} />,
  'fmt-monthly': ({ tasks, onCreateTasks }) => (
    <MonthlyAccounting existingTasks={tasks} onCreateTasks={onCreateTasks} />
  ),
  'monthly-progress': ({ tasks, onUpdateTask, onDeleteTask }) => (
    <MonthlyProgress tasks={tasks} onUpdateTask={onUpdateTask} onDeleteTask={onDeleteTask} />
  ),
  // 決算タスクFMT は月次経理と同じ画面を、決算の保存先・種別で使う
  'fmt-closing': ({ tasks, onCreateTasks }) => (
    <MonthlyAccounting existingTasks={tasks} onCreateTasks={onCreateTasks} variant={CLOSING_FMT} />
  ),
  'closing-progress': ({ tasks, onUpdateTask, onDeleteTask }) => (
    <MonthlyProgress
      tasks={tasks}
      onUpdateTask={onUpdateTask}
      onDeleteTask={onDeleteTask}
      variant={CLOSING_FMT}
    />
  ),
  'related-links': ({ team }) => <RelatedLinks team={team} />,
  'members-settings': ({ tasks }) => <MembersSettings tasks={tasks} />,
  'kinds-settings': ({ tasks }) => <KindsSettings tasks={tasks} />,
};

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business アプリの外枠。左のサイドバーで機能を選び、右に選んだ画面を出す
 * @graph-connects TeamSelect [calls] 最初のチーム選択を描画する
 * @graph-connects Sidebar [calls] メニューを描画する
 * @graph-connects TaskManagement [calls] タスク管理の画面を描画する
 * @graph-connects TaskAnalysis [calls] タスク分析の画面を描画する
 * @graph-connects MonthlyAccounting [calls] FMT の月次経理の画面を描画する
 * @graph-connects MonthlyProgress [calls] 月次経理の進捗の画面を描画する
 * @graph-connects RelatedLinks [calls] 関連リンクの画面を描画する
 * @graph-connects MembersSettings [calls] メンバー設定の画面を描画する
 * @graph-connects KindsSettings [calls] 種別設定の画面を描画する
 */
export function App({ initialMenu = 'tasks', initialTeam }: AppProps = {}) {
  const [menu, setMenu] = useState<MenuId>(initialMenu);
  // 前回選んだチームを覚えているならそれで開く。毎回選ばせない
  const [team, setTeam] = useState<TeamId | null>(initialTeam ?? readRememberedTeam());

  const selectTeam = (next: TeamId) => {
    setTeam(next);
    rememberTeam(next);
    // 切り替え先のチームで開けない画面に留まらないよう、タスク管理へ戻す
    if (!isMenuAvailable(menu, next)) {
      setMenu('tasks');
    }
  };

  // まだチームを選んでいなければ、選ぶところから始める
  if (team === null) {
    return <TeamSelect onSelect={selectTeam} />;
  }

  return (
    <AppShell
      team={team}
      menu={menu}
      onSelectMenu={setMenu}
      onGoHome={() => {
        // 覚えている前回のチームはそのままにする。選び直したいだけで、
        // 次に開いたときの既定まで消えると毎回選ばせることになる
        setTeam(null);
      }}
    />
  );
}

/**
 * AppShell の props。
 */
interface AppShellProps {
  /** 選択中のチーム */
  team: TeamId;
  /** 選択中のメニュー */
  menu: MenuId;
  /** メニューを選んだときに呼ばれる */
  onSelectMenu: (menu: MenuId) => void;
  /** ホーム（チームを選ぶ画面）へ戻るときに呼ばれる */
  onGoHome: () => void;
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business チームが決まったあとの画面。サイドバーと選んだ画面を出し、タスクの読み書きを API に繋ぐ
 * @graph-connects useTasks [calls] タスクを読み書きする
 * @graph-connects Sidebar [calls] メニューを描画する
 */
function AppShell({ team, menu, onSelectMenu, onGoHome }: AppShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { tasks, loading, error, reload, createMany, update, remove } = useTasks(team);

  // 画面の高さに固定する。カレンダーに縦を最大限渡すため、スクロールは各画面の中で持つ
  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        current={menu}
        onSelect={onSelectMenu}
        collapsed={sidebarCollapsed}
        onToggle={() => {
          setSidebarCollapsed(!sidebarCollapsed);
        }}
        team={team}
        onGoHome={onGoHome}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* 読み込み中と失敗は画面の上に出す。黙ってサンプルに切り替えると、
            繋がっていないことに気づけない */}
        {loading ? (
          <p className="m-0 bg-[var(--color-bg-subtle)] px-8 py-1.5 text-xs text-[var(--color-text-muted)]">
            読み込んでいます…
          </p>
        ) : null}
        {error === '' ? null : (
          <p className="m-0 flex flex-wrap items-center gap-3 bg-[var(--color-doing-bg)] px-8 py-1.5 text-xs text-[var(--color-doing-text)]">
            {error}
            <button
              type="button"
              onClick={reload}
              className="cursor-pointer rounded border border-[var(--color-doing-border)] px-2 py-0.5"
            >
              読み込み直す
            </button>
          </p>
        )}

        {/* 自動化は 10 項目とも同じ流れなので、個別の画面を作らず 1 つで賄う */}
        {menu in AUTOMATIONS ? (
          <AutomationRun id={menu as AutomationId} />
        ) : (
          (PAGES[menu]?.({
            team,
            tasks,
            onCreateTasks: createMany,
            onUpdateTask: update,
            onDeleteTask: remove,
            onReloadTasks: reload,
          }) ?? null)
        )}
      </div>
    </div>
  );
}
